import React, { useEffect, useRef } from "react";
import grapesjs from "grapesjs";
import "grapesjs/dist/css/grapes.min.css";

// Optional plugins — include only if relevant
import mjmlPlugin from "grapesjs-mjml";
import blocksBasic from "grapesjs-blocks-basic";

export default function GrapesJSEngine({
    value = "",
    onChange,
    height = 600,
    placeholder = "",
    type = "layout",
    config = {},
    className,
    disabled = false,
    ...rest
}) {
    const editorRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return;

        // Destroy previous instance if remounted
        if (editorRef.current) {
            editorRef.current.destroy();
            editorRef.current = null;
        }

        // Initialize GrapesJS
        const editor = grapesjs.init({
            container: containerRef.current,
            fromElement: false,
            height: height,
            width: "100%",
            storageManager: false,
            noticeOnUnload: false,
            allowScripts: 1,
            plugins: [blocksBasic, ...(config.plugins || []), mjmlPlugin],
            pluginsOpts: {
                [blocksBasic]: {},
                [mjmlPlugin]: {},
                ...(config.pluginsOpts || {}),
            },
            canvas: {
                styles: [
                    "https://cdnjs.cloudflare.com/ajax/libs/meyer-reset/2.0/reset.min.css",
                    ...(config.canvas?.styles || []),
                ],
                scripts: [...(config.canvas?.scripts || [])],
            },
            blockManager: config.blockManager || {},
            styleManager: config.styleManager || {},
            traitManager: config.traitManager || {},
            deviceManager: config.deviceManager || {},
            ...config.initOverrides,
        });

        editorRef.current = editor;

        // Load initial content
        if (value) {
            if (type === "email" && value.trim().startsWith("<mjml")) {
                // MJML mode: import MJML source
                editor.setComponents(value);
            } else {
                editor.setComponents(value);
            }
        } else if (placeholder) {
            editor.setComponents(`<div>${placeholder}</div>`);
        }

        // Change listener
        editor.on("update", () => {
            const html = editor.getHtml();
            const css = editor.getCss();
            if (onChange) onChange({ html, css, full: html + `<style>${css}</style>` });
        });

        // Disable interactions if needed
        if (disabled) {
            editor.Canvas.getBody().style.pointerEvents = "none";
            editor.Canvas.getBody().style.opacity = 0.7;
        }

        // Custom UI tweaks or plugin setup
        if (typeof config.setup === "function") config.setup(editor);

        return () => {
            editor.destroy();
            editorRef.current = null;
        };
    }, [containerRef, value, disabled]);

    return (
        <div
            ref={containerRef}
            className={className || "grapesjs-container"}
            style={{
                height,
                width: "100%",
                border: "1px solid #ddd",
                borderRadius: 6,
                overflow: "hidden",
            }}
            {...rest}
        />
    );
}
